import * as React from 'react';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { AddonsSuccessScreen } from './components/AddonsSuccessScreen/AddonsSuccessScreen';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { BusinessShieldContainer } from './containers/BusinessShield/BusinessShieldContainer';
import { CompanyProfileInfoGuard } from '@pec/aion-ui-components/containers/CompanyProfileInfoGuard';
import { ErrorBoundary } from '@pec/aion-ui-core/components/ErrorBoundary';
import { EstimateOneRedirect } from 'containers/EstimateOne/EstimateOneRedirect';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { Footer } from '@pec/aion-ui-components/containers/Footer';
import { GtmWrapper } from '@pec/aion-ui-core/components/GtmWrapper';
import { HasPermissionContainer } from '@pec/aion-ui-components/containers/HasPermission';
import { isUUID } from './helpers/helpers';
import { MainPage } from './containers/MainPage';
import { MembershipPage } from './containers/Membership/MembershipPage';
import { NavContainer } from '@pec/aion-ui-components/containers/Nav';
import { NgqeUsCohortGuard } from './containers/NgqeUsCohortGuard';
import { NotifierContainer } from '@pec/aion-ui-components/containers/Notifier';
import { OnlineTrainingContainer } from 'containers/OnlineTraining/OnlineTraining';
import { OperationType } from './surveyjs/common/types/enums';
import { PartnerPage } from './containers/EstimateOne/Partner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Provider } from 'react-redux';
import { QuestionnaireContainer } from 'containers/Questionnaire';
import { QuestionnaireRedirectContainer } from 'containers/Questionnaire/QuestionnaireRedirect';
import { RegisterInterceptors } from '@pec/aion-ui-components/containers/RegisterInterceptors';
import { RouteRedirect } from './components/NewDashboard/RouteRedirect';
import { SnackbarProvider } from 'notistack';
import { store } from 'store';
import { SuccessScreen } from './surveyjs/common/components/SuccessScreen';
import { SurveyPage } from './surveyjs/common/containers/SurveyPage';
import { SuspenseWrapper } from './containers/SuspenceWrapper/SuspenseWrapper';
import { Theme } from '@pec/aion-ui-components/components/Theme';
import { Trackers } from '@pec/aion-ui-core/containers/Trackers';
import { TrackerWrapper } from '@pec/aion-ui-core/components/TrackerWrapper';
import { useTranslation } from 'react-i18next';

const { Manage, Write, Read } = ActivityAction;
const { LegacyQuestionnaire, QuestionnaireAdmin, Subscriptions } = ActivityResourceName;

export const App: React.FC = () => {
  const { t } = useTranslation();
  const organizationIdParam = window.location.pathname.split('/')[2];

  const ABSOLUTE_LINKS = [
    '/online-training',
    '/business-shield',
    '/social-sustainability',
    '/vehicle-compliance',
    '/scope-change'
  ];
  const LazyVCSComponent = React.lazy(() => import('./containers/VehicleCompliance/index'));
  const LazyESGComponent = React.lazy(() => import('./containers/SocialSustainability/index'));

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <SnackbarProvider
          maxSnack={3}
          preventDuplicate
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
        >
          <>
            <NotifierContainer />
            <RegisterInterceptors organizationId={isUUID(organizationIdParam) ? organizationIdParam : ''}>
              <GtmWrapper>
                <TrackerWrapper>
                  <Router basename="/organizations">
                    <Theme>
                      {process.env.NODE_ENV === 'production' && <Route component={Trackers} />}
                      <Route path="/partners/estimateone" component={EstimateOneRedirect} />
                      <Route
                        path="/:organizationId?"
                        render={({
                          location,
                          match: {
                            params: { organizationId }
                          }
                        }) => {
                          if (!isUUID(organizationId) && ABSOLUTE_LINKS.includes(location.pathname)) {
                            return (
                              <Switch>
                                <Route
                                  path="/online-training"
                                  render={() => <RouteRedirect path="/online-training" />}
                                />
                                <Route
                                  path="/business-shield"
                                  render={() => <RouteRedirect path="/business-shield" />}
                                />
                                <Route
                                  path="/social-sustainability"
                                  render={() => <RouteRedirect path="/social-sustainability" />}
                                />
                                <Route
                                  path="/vehicle-compliance"
                                  render={() => <RouteRedirect path="/vehicle-compliance" />}
                                />
                                <Route path="/scope-change" render={() => <RouteRedirect path="/scope-change" />} />
                              </Switch>
                            );
                          }

                          return (
                            <NavContainer
                              organizationId={organizationId}
                              title={t('subscriber:compliancePro', 'Compliance Pro')}
                              location={location}
                            >
                              <FeatureFlagWrapper>
                                <HasPermissionContainer>
                                  {({ hasOrganizationPermission, hasGlobalPermission }) => (
                                    <Switch>
                                      {hasGlobalPermission(Manage, QuestionnaireAdmin) && (
                                        <Route
                                          path="/:organizationId/questionnaire/:questionnaireSectionId"
                                          render={() => (
                                            <NgqeUsCohortGuard>
                                              <CompanyProfileInfoGuard
                                                isEnabled={!hasOrganizationPermission(Manage, LegacyQuestionnaire)}
                                                organizationId={organizationId}
                                              >
                                                <QuestionnaireContainer isEnabled={true} />
                                              </CompanyProfileInfoGuard>
                                            </NgqeUsCohortGuard>
                                          )}
                                        />
                                      )}
                                      {hasGlobalPermission(Manage, QuestionnaireAdmin) && (
                                        <Route
                                          path="/:organizationId/questionnaire"
                                          render={() => (
                                            <NgqeUsCohortGuard>
                                              <CompanyProfileInfoGuard
                                                isEnabled={!hasOrganizationPermission(Manage, LegacyQuestionnaire)}
                                                organizationId={organizationId}
                                              >
                                                <QuestionnaireRedirectContainer isEnabled={true} />
                                              </CompanyProfileInfoGuard>
                                            </NgqeUsCohortGuard>
                                          )}
                                        />
                                      )}
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        activityAction={Write}
                                        path="/:organizationId/upgrade/success"
                                        component={SuccessScreen}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        activityAction={Write}
                                        path="/:organizationId/renew/success"
                                        component={SuccessScreen}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        activityAction={Read}
                                        path="/:organizationId/scope-change/success"
                                        component={SuccessScreen}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        activityAction={Write}
                                        path="/:organizationId/upgrade"
                                        render={() => <SurveyPage operationType={OperationType.Upgrade} />}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        activityAction={Write}
                                        path="/:organizationId/renew"
                                        render={() => <SurveyPage operationType={OperationType.Renew} />}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        activityAction={Read}
                                        path="/:organizationId/scope-change"
                                        render={() => <SurveyPage operationType={OperationType.ScopeChange} />}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        path="/:organizationId/membership"
                                        component={MembershipPage}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        path="/:organizationId/partners/:partnerId"
                                        component={PartnerPage}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        path="/:organizationId/online-training"
                                        component={OnlineTrainingContainer}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        path="/:organizationId/business-shield"
                                        component={BusinessShieldContainer}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        path={'/:organizationId/social-sustainability/success'}
                                        component={AddonsSuccessScreen}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        path={'/:organizationId/vehicle-compliance/success'}
                                        component={AddonsSuccessScreen}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        path="/:organizationId/social-sustainability"
                                        render={() => (
                                          <SuspenseWrapper>
                                            <LazyESGComponent />
                                          </SuspenseWrapper>
                                        )}
                                      />
                                      <ProtectedRoute
                                        permissionResource={Subscriptions}
                                        path="/:organizationId/vehicle-compliance"
                                        render={() => (
                                          <SuspenseWrapper>
                                            <LazyVCSComponent />
                                          </SuspenseWrapper>
                                        )}
                                      />
                                      <Route path="/:organizationId" component={MainPage} />
                                      <Route path="/" render={() => null} />
                                    </Switch>
                                  )}
                                </HasPermissionContainer>
                              </FeatureFlagWrapper>
                              <Footer />
                            </NavContainer>
                          );
                        }}
                      />
                    </Theme>
                  </Router>
                </TrackerWrapper>
              </GtmWrapper>
            </RegisterInterceptors>
          </>
        </SnackbarProvider>
      </Provider>
    </ErrorBoundary>
  );
};
